/**
 * Open rent from PAST billing periods (COL-435).
 *
 * The reminders surfaces judge one thing per booking: is the tenant's CURRENT
 * billing period covered (`isPaidCurrentPeriod`)? When the month rolls over
 * and the tenant pays the new month, an unpaid previous month drops off every
 * dashboard — nothing lists it, nothing counts it, nothing reminds about it.
 *
 * The ledger (`public.payments`, one rent row per month with `due_date` = the
 * month it pays for) still knows. This module turns those rows into "past
 * month" items using the same rules the tenant Payments tab already applies:
 *
 *   - paid   = the canonical `isPaymentEffectivelyPaid` (Stripe, approved
 *              proof, or admin attestation) — a bare `status = 'paid'` with
 *              nothing behind it is a claim, not a payment;
 *   - void   = `canceled` only. A `failed` SEPA debit is still money owed and
 *              must stay actionable (mirrors `splitRentRows`);
 *   - covered month = a month with an effectively-paid row is never listed,
 *              even if a stale pending duplicate exists for it (mirrors
 *              `selectEarliestUnpaidRentPayment`);
 *   - past   = strictly before the booking's CURRENT billing period. That
 *              period is `currentPeriodDueDate(rent_due_date)`'s month, not
 *              the calendar month: a tenant with due day 15 is still in
 *              August's period on 8 September, and August is already the
 *              current-period row on the dashboard. Listing it again as a
 *              past month would double-count.
 *
 * Pure: no client, no dates other than the injected `now`. Every surface that
 * shows or counts past months (RemindersTab, AdminReminders, the property
 * badges) must resolve through `selectOpenPastMonthRent` so they cannot
 * disagree with each other.
 */
import { isPaymentEffectivelyPaid } from "./rent";
import {
  currentPeriodDueDate,
  type ReminderStage,
} from "./rent-reminder-templates";

export interface RentLedgerRow {
  id: string | number;
  booking_id: number | null;
  type?: string | null;
  status?: string | null;
  due_date?: string | null;
  amount_cents?: number | null;
  /** COL-417: cents already received against this row (payments.received_cents). */
  received_cents?: number | null;
  stripe_payment_intent_id?: string | null;
  stripe_charge_id?: string | null;
  marked_paid_at?: string | null;
  marked_paid_by?: string | null;
}

export interface OpenPastMonth {
  /** The ledger row this item is backed by. */
  payment_id: string;
  booking_id: number;
  /** ISO date (YYYY-MM-DD) the ledger row is due — the month it pays for. */
  due_date: string;
  /** `YYYY-MM` of `due_date`. */
  ym: string;
  /** What the month BILLS, in EUR — `amount_cents`, unchanged by any receipt. */
  amount_eur: number;
  /**
   * COL-417: what is still OPEN on the month, in EUR — billed minus received.
   *
   * Kept separate from `amount_eur` rather than replacing it: a reminder must
   * demand what is still owed, but the invoice figure is what the month was
   * billed at, and a surface that needs one and renders the other is exactly
   * the bug this pair exists to prevent. Equal to `amount_eur` for every month
   * nobody part-paid, and for every caller that does not select
   * `received_cents`.
   */
  open_eur: number;
  /** COL-417: cents already received, in EUR. Zero when nothing arrived. */
  received_eur: number;
  /** Whole days between `due_date` and `now` (UTC), never negative. */
  days_overdue: number;
}

const MS_PER_DAY = 86_400_000;

/** Canceled rows are retired attempts — never a debt, never a receipt. */
const isVoid = (r: RentLedgerRow) =>
  String(r.status ?? "").toLowerCase() === "canceled";

/** A ledger row with no `type` predates the column and is rent. */
const isRent = (r: RentLedgerRow) =>
  r.type == null || String(r.type).toLowerCase() === "rent";

/** `YYYY-MM` of an ISO date string ("" when missing). */
export function ymOf(iso: string | null | undefined): string {
  return String(iso ?? "").slice(0, 7);
}

/**
 * `YYYY-MM` of the booking's current billing period. A missing/invalid due
 * day behaves like the 1st, i.e. the calendar month.
 */
export function currentPeriodYm(
  rentDueDay: number | null | undefined,
  now: Date = new Date()
): string {
  const day =
    rentDueDay != null && rentDueDay >= 1 && rentDueDay <= 31 ? rentDueDay : 1;
  return currentPeriodDueDate(day, now).toISOString().slice(0, 7);
}

/** Whole UTC days from `dueIso` to `now`, clamped at 0. */
export function daysSince(dueIso: string, now: Date = new Date()): number {
  const due = Date.parse(dueIso);
  if (!Number.isFinite(due)) return 0;
  const todayUtc = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate()
  );
  return Math.max(0, Math.floor((todayUtc - due) / MS_PER_DAY));
}

/**
 * The stage a reminder about a PAST month is sent at: one step on from the
 * booking's stored `reminder_stage`, capped at 4 (final notice). Product
 * decision (Neel, 2026-09-08): the dunning chain continues from where the
 * booking is, rather than recomputing from the old due date — which would
 * always land on final notice because a past month is always 9+ days late.
 */
export function nextStageInChain(
  reminderStage: number | null | undefined
): ReminderStage {
  const last = Math.min(4, Math.max(0, Math.trunc(Number(reminderStage) || 0)));
  return Math.min(4, last + 1) as ReminderStage;
}

/**
 * Select the open past-month rent rows across bookings, oldest first.
 *
 * `rentDueDayByBooking` supplies each booking's `rent_due_date`; bookings
 * missing from the map fall back to the calendar month. Rows for bookings not
 * in `bookingIds` (when given) are ignored, so callers can scope to the
 * tenants they actually track (occupying now, reminders enabled).
 */
export function selectOpenPastMonthRent(input: {
  rows: readonly RentLedgerRow[];
  proofFor: (
    paymentId: string
  ) => { status?: string | null } | null | undefined;
  rentDueDayByBooking: ReadonlyMap<number, number | null>;
  bookingIds?: ReadonlySet<number>;
  now?: Date;
}): OpenPastMonth[] {
  const now = input.now ?? new Date();

  const byBooking = new Map<number, RentLedgerRow[]>();
  for (const r of input.rows) {
    if (r.booking_id == null || !isRent(r) || !r.due_date) continue;
    if (input.bookingIds && !input.bookingIds.has(r.booking_id)) continue;
    const list = byBooking.get(r.booking_id) ?? [];
    list.push(r);
    byBooking.set(r.booking_id, list);
  }

  const out: OpenPastMonth[] = [];
  for (const [bookingId, rows] of byBooking) {
    const cutoffYm = currentPeriodYm(
      input.rentDueDayByBooking.get(bookingId) ?? null,
      now
    );
    const paidMonths = new Set<string>();
    for (const r of rows) {
      if (isVoid(r)) continue;
      if (isPaymentEffectivelyPaid(r, input.proofFor))
        paidMonths.add(ymOf(r.due_date));
    }
    const seen = new Set<string>();
    const open = rows
      .filter((r) => {
        const ym = ymOf(r.due_date);
        if (!ym || ym >= cutoffYm) return false;
        if (isVoid(r) || paidMonths.has(ym)) return false;
        if (isPaymentEffectivelyPaid(r, input.proofFor)) return false;
        return (Number(r.amount_cents) || 0) > 0;
      })
      .sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)));
    for (const r of open) {
      const ym = ymOf(r.due_date);
      // One item per month: a stale duplicate pending row for the same month
      // is the same debt, not a second one.
      if (seen.has(ym)) continue;
      seen.add(ym);
      const billedCents = Math.round(Number(r.amount_cents) || 0);
      // Clamped at the billed amount: an overpayment is carried forward as a
      // credit on the next open month, never as a negative balance here.
      const rawReceived = Math.round(Number(r.received_cents) || 0);
      const receivedCents = Math.min(
        billedCents,
        Number.isFinite(rawReceived) && rawReceived > 0 ? rawReceived : 0
      );
      out.push({
        payment_id: String(r.id),
        booking_id: bookingId,
        due_date: String(r.due_date),
        ym,
        amount_eur: billedCents / 100,
        open_eur: Math.max(0, billedCents - receivedCents) / 100,
        received_eur: receivedCents / 100,
        days_overdue: daysSince(String(r.due_date), now),
      });
    }
  }
  out.sort((a, b) => a.due_date.localeCompare(b.due_date));
  return out;
}

/**
 * Where a booking's Mahngebühren are DISPLAYED (COL-435).
 *
 * `bookings.accumulated_fees_eur` is one fee pool per tenant. On the reminders
 * surfaces it must appear on exactly one row so totals never double-count and
 * never drop it: the current-period row while that row is overdue, otherwise
 * the oldest open past month. Returns the fee amount to show per row; rows not
 * in the result show 0. Pure — both admin surfaces resolve through this.
 */
export function placeBookingFees(
  currentRows: readonly {
    booking_id: number;
    payment_status: string | null;
    current_stage: number;
    accumulated_fees_eur: number;
  }[],
  pastMonths: readonly Pick<OpenPastMonth, "payment_id" | "booking_id">[]
): {
  /** booking_id → fees to show on the current-period row */
  currentRowFees: Map<number, number>;
  /** payment_id of the past-month row → fees to show on it */
  pastRowFees: Map<string, number>;
} {
  const currentRowFees = new Map<number, number>();
  const pastRowFees = new Map<string, number>();
  const oldestPastByBooking = new Map<string | number, string>();
  // pastMonths is oldest-first (selectOpenPastMonthRent sorts by due_date).
  for (const m of pastMonths) {
    if (!oldestPastByBooking.has(m.booking_id))
      oldestPastByBooking.set(m.booking_id, m.payment_id);
  }
  for (const r of currentRows) {
    const fees = Number(r.accumulated_fees_eur) || 0;
    const currentOverdue = r.payment_status !== "paid" && r.current_stage > 0;
    const oldestPast = oldestPastByBooking.get(r.booking_id);
    if (!currentOverdue && oldestPast !== undefined) {
      pastRowFees.set(oldestPast, fees);
      currentRowFees.set(r.booking_id, 0);
    } else {
      currentRowFees.set(r.booking_id, fees);
    }
  }
  return { currentRowFees, pastRowFees };
}

/** COL-417: the current period's own invoice figures, straight from the ledger. */
export type CurrentPeriodLedger = {
  /** Concrete due date of the current invoice (move-in date for first rent). */
  dueDate: string;
  /** What the current month's ledger row bills — `payments.amount_cents`. */
  billedCents: number;
  /** Received against it, clamped at what it bills. */
  receivedCents: number;
};

/**
 * Remove only a booking's synthetic current-period row when no concrete
 * invoice exists for that period. Historical open rows remain visible.
 */
export function keepInvoicedCurrentRows<
  T extends { booking_id: number; period_kind: "current" | "past" },
>(
  rows: readonly T[],
  currentInvoices: ReadonlyMap<number, unknown>,
  trackedBookings: ReadonlySet<number>
): T[] {
  return rows.filter(
    (row) =>
      row.period_kind !== "current" ||
      !trackedBookings.has(row.booking_id) ||
      currentInvoices.has(row.booking_id)
  );
}

/**
 * Fee placement still needs one current-row candidate per booking, including
 * bookings whose synthetic current row is hidden because no invoice exists.
 * Treat those absent periods as not overdue so their booking-level fees move
 * to the oldest real open month instead of disappearing with the hidden row.
 */
export function currentRowsForFeePlacement<
  T extends {
    booking_id: number;
    payment_status: string | null;
    current_stage: number;
  },
>(rows: readonly T[], currentInvoices: ReadonlyMap<number, unknown>): T[] {
  return rows.map((row): T =>
    currentInvoices.has(row.booking_id)
      ? row
      : { ...row, payment_status: "pending", current_stage: 0 }
  );
}

/**
 * COL-417: each booking's CURRENT billing period as the ledger has it, keyed by
 * booking id.
 *
 * The past-month rows carry their own invoice figures because they are built
 * from ledger rows. The current-period row is not — both reminder surfaces
 * derive it from the booking (`effectiveRentForMonthEUR`), which knows what the
 * booking charges and nothing about what this month's invoice actually says or
 * what arrived against it. Without this the current month is the one row on the
 * whole screen that still demands the full rent after a partial payment.
 *
 * Returns BOTH figures, not just the received one, because the caller nets them
 * against each other. Taking `received` from the ledger and `billed` from the
 * booking mixes two sources that disagree the moment a month's `amount_cents`
 * is edited: an EUR 800 invoice on an EUR 650 booking with EUR 700 received
 * nets to zero and the reminder demands nothing while EUR 100 is open — a
 * silent zero on a dunning surface. The two figures have to come from the same
 * row, which is the same billed/open split `selectOpenPastMonthRent` already
 * makes with `amount_eur` vs `open_eur`.
 *
 * Reads the same ledger the past-month selection already fetched, so this costs
 * no extra query. Months other than the current one are ignored: a receipt on
 * August must not reduce what September asks for. A booking with no
 * materialized row for the current month is absent from the map, and reminder
 * surfaces omit that synthetic current row while retaining open history.
 *
 * A failed SEPA attempt and its retry are two ledger records for one invoice.
 * Prefer the live retry; use the failed row only when no replacement exists.
 * This mirrors the automatic dunning path and prevents totals, exports and
 * draft emails from billing the same month twice.
 */
export function currentPeriodLedger(
  rows: readonly RentLedgerRow[],
  rentDueDayByBooking: ReadonlyMap<number, number | null>,
  now: Date = new Date()
): Map<number, CurrentPeriodLedger> {
  const out = new Map<number, CurrentPeriodLedger>();
  const selectedStatus = new Map<number, string>();
  for (const r of rows ?? []) {
    const bookingId = r?.booking_id;
    if (bookingId == null) continue;
    if (isVoid(r) || !isRent(r)) continue;
    const ym = ymOf(r.due_date);
    if (!ym) continue;
    if (ym !== currentPeriodYm(rentDueDayByBooking.get(bookingId), now))
      continue;
    const status = String(r.status ?? "").toLowerCase();
    const previousStatus = selectedStatus.get(bookingId);
    if (previousStatus != null) {
      const previousFailed = previousStatus === "failed";
      const candidateFailed = status === "failed";
      // A failed SEPA attempt and its live retry represent one invoice. The
      // live row wins; a failed row is used only when there is no replacement.
      if (!previousFailed || candidateFailed) continue;
    }
    const billed = Math.max(0, Math.round(Number(r.amount_cents) || 0));
    const rawReceived = Math.round(Number(r.received_cents) || 0);
    const received =
      Number.isFinite(rawReceived) && rawReceived > 0 ? rawReceived : 0;
    // Clamped at what the row bills — the surplus is a carried-forward credit
    // on the next month, not a discount on this one.
    const capped = billed > 0 ? Math.min(billed, received) : received;
    out.set(bookingId, {
      dueDate: String(r.due_date),
      billedCents: billed,
      receivedCents: capped,
    });
    selectedStatus.set(bookingId, status);
  }
  return out;
}
